import { createHash } from 'node:crypto'
import { glob, mkdtemp, readFile, rm } from 'node:fs/promises'
import { tmpdir } from 'node:os'
import { join, sep } from 'node:path'
import * as tar from 'tar'
import { parse } from 'yaml'
import type { DsseEnvelope } from '../crypto/dsse.js'
import type { EncryptionManifest } from '../crypto/seal.js'

export interface VpkgContents {
  metadata: { name: string; publisher: string; version: string; kind: string; 'display-name'?: string }
  manifestYaml: string
  digestsJson: string
  treeDigest: string
  developerSignature?: DsseEnvelope
  marketplaceSignature?: DsseEnvelope
  encryption?: EncryptionManifest
  files: string[]
  /** Digest problems discovered while reading ([] = digest-clean). */
  digestErrors: string[]
}

/** Extracts and digest-checks a .vpkg (mirrors the registry's read/validate pass). */
export async function readVpkg(vpkgPath: string): Promise<VpkgContents> {
  const staging = await mkdtemp(join(tmpdir(), 'vpkg-read-'))
  try {
    await tar.extract({ file: vpkgPath, cwd: staging })

    const manifestYaml = await readFile(join(staging, 'manifest.yaml'), 'utf8').catch(() => {
      throw new Error('Invalid package: missing manifest.yaml.')
    })
    const packageYaml = await readFile(join(staging, '.verentis', 'package.yaml'), 'utf8').catch(() => {
      throw new Error('Invalid package: missing .verentis/package.yaml.')
    })
    const digestsJson = await readFile(join(staging, '.verentis', 'digests.json'), 'utf8').catch(() => {
      throw new Error('Invalid package: missing .verentis/digests.json.')
    })

    const metadata = parse(packageYaml) as VpkgContents['metadata']
    const declared = JSON.parse(digestsJson) as { entries?: Record<string, string> }

    const files: string[] = []
    for await (const entry of glob('files/**/*', { cwd: staging })) {
      const abs = join(staging, entry)
      const info = await readFile(abs).catch(() => null)
      if (info === null) continue // directory
      files.push(entry.split(sep).join('/'))
    }
    files.sort()

    const digestErrors: string[] = []
    const entries = declared.entries ?? {}
    const manifestDigest = createHash('sha256').update(manifestYaml, 'utf8').digest('hex')
    if (entries['manifest.yaml'] && entries['manifest.yaml'].toLowerCase() !== manifestDigest) {
      digestErrors.push('Digest mismatch for manifest.yaml.')
    }
    for (const file of files) {
      const declaredDigest = entries[file]
      if (!declaredDigest) {
        digestErrors.push(`Entry '${file}' is not covered by digests.json.`)
        continue
      }
      const actual = createHash('sha256').update(await readFile(join(staging, file))).digest('hex')
      if (actual !== declaredDigest.toLowerCase()) digestErrors.push(`Digest mismatch for '${file}'.`)
    }
    for (const declaredPath of Object.keys(entries)) {
      if (declaredPath !== 'manifest.yaml' && !files.includes(declaredPath)) {
        digestErrors.push(`Digest declared for missing entry '${declaredPath}'.`)
      }
    }

    const developerSignature = await readJsonIfExists<DsseEnvelope>(join(staging, '.verentis', 'signatures', 'developer.dsse.json'))
    const marketplaceSignature = await readJsonIfExists<DsseEnvelope>(join(staging, '.verentis', 'signatures', 'marketplace.dsse.json'))
    const encryption = await readJsonIfExists<EncryptionManifest>(join(staging, '.verentis', 'encryption.json'))

    return {
      metadata,
      manifestYaml,
      digestsJson,
      treeDigest: createHash('sha256').update(digestsJson, 'utf8').digest('hex'),
      developerSignature: developerSignature ?? undefined,
      marketplaceSignature: marketplaceSignature ?? undefined,
      encryption: encryption ?? undefined,
      files: files.map((f) => f.replace(/^files\//, '')),
      digestErrors,
    }
  } finally {
    await rm(staging, { recursive: true, force: true })
  }
}

async function readJsonIfExists<T>(path: string): Promise<T | null> {
  try {
    return JSON.parse(await readFile(path, 'utf8')) as T
  } catch {
    return null
  }
}
